import { z } from 'zod';
/**
 * Contrats du domaine « opérations » : vue de situation counter-UAS (ADR-0040).
 *
 * Deux faces : l'**ingestion** (ce qu'un système source — simulateur aujourd'hui, détecteur/C2
 * demain — pousse via l'API sécurisée) et la **lecture** (l'état consolidé que lit l'IHM). Les
 * coordonnées sont en degrés WGS 84, les vitesses en m/s, les caps en degrés (0 = nord, sens horaire).
 */
export const threatLevelSchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export const threatClassSchema = z.enum([
    'UNKNOWN',
    'MULTIROTOR',
    'FIXED_WING',
    'HELICOPTER',
    'OTHER',
]);
export const defenderStatusSchema = z.enum([
    'AVAILABLE',
    'ENGAGED',
    'RETURNING',
    'CHARGING',
    'OFFLINE',
]);
const latitude = z.number().min(-90).max(90);
const longitude = z.number().min(-180).max(180);
const heading = z.number().min(0).max(360);
/* ------------------------------------------------------------------ *
 * Zones
 * ------------------------------------------------------------------ */
export const createOperationalZoneSchema = z.object({
    name: z.string().trim().min(2).max(150),
    centerLat: latitude,
    centerLon: longitude,
    /** Rayon défendu, en mètres (borné à 500 km : au-delà ce n'est plus une zone). */
    radiusM: z.number().positive().max(500_000),
});
export const operationalZoneSchema = z.object({
    id: z.string(),
    name: z.string(),
    centerLat: z.number(),
    centerLon: z.number(),
    radiusM: z.number(),
    /** Démo (ADR-0040 §C5) : le worker injecte des menaces simulées quand ce fanion est vrai. */
    simulationEnabled: z.boolean(),
    createdAt: z.coerce.date(),
});
/**
 * Mise à jour partielle d'une zone (EDITOR+) : renommage, recentrage, rayon, et **armement de la
 * simulation** depuis l'IHM. Au moins un champ requis (une requête vide ne veut rien dire).
 */
export const updateOperationalZoneSchema = z
    .object({
    name: z.string().trim().min(2).max(150).optional(),
    centerLat: latitude.optional(),
    centerLon: longitude.optional(),
    radiusM: z.number().positive().max(500_000).optional(),
    simulationEnabled: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((field) => field !== undefined), {
    message: 'Au moins un champ doit être fourni.',
});
/* ------------------------------------------------------------------ *
 * Lecture : menaces, défenseurs, image consolidée
 * ------------------------------------------------------------------ */
export const threatSchema = z.object({
    id: z.string(),
    zoneId: z.string(),
    externalRef: z.string(),
    class: threatClassSchema,
    level: threatLevelSchema,
    sizeM: z.number().nullable(),
    lat: z.number(),
    lon: z.number(),
    altitudeM: z.number().nullable(),
    speedMps: z.number().nullable(),
    headingDeg: z.number().nullable(),
    firstSeenAt: z.coerce.date(),
    lastSeenAt: z.coerce.date(),
});
export const defenderSchema = z.object({
    id: z.string(),
    zoneId: z.string().nullable(),
    assetId: z.string(),
    /** Nom du matériel (joint pour l'affichage, sans requête par défenseur). */
    assetName: z.string(),
    status: defenderStatusSchema,
    lat: z.number().nullable(),
    lon: z.number().nullable(),
    altitudeM: z.number().nullable(),
    headingDeg: z.number().nullable(),
    batteryPct: z.number().int().nullable(),
    updatedAt: z.coerce.date(),
});
/** État consolidé d'une zone, lu par l'IHM opérationnelle. */
export const operationalPictureSchema = z.object({
    zone: operationalZoneSchema,
    threats: z.array(threatSchema),
    defenders: z.array(defenderSchema),
    /** Horodatage de production côté serveur (sert au suivi de fraîcheur côté IHM). */
    generatedAt: z.coerce.date(),
});
/* ------------------------------------------------------------------ *
 * Gestion des défenseurs par l'opérateur (ADR-0040, C3) — écriture EDITOR+
 *
 * Distincte de l'ingestion M2M : ici un opérateur **rattache** un matériel de la flotte comme
 * défenseur d'une zone, puis en tient l'état et la position à la main. La position reste découplée
 * d'un flux de télémétrie (ADR-0040 §3) — elle peut être posée directement ou laissée vide.
 * ------------------------------------------------------------------ */
/** Matériel candidat au rôle de défenseur : un DRONE/UGV de la flotte pas encore rattaché. */
export const operationalAssetSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: z.enum(['DRONE', 'UGV']),
});
/** Rattache un matériel existant comme défenseur d'une zone. Position optionnelle. */
export const createDefenderSchema = z.object({
    assetId: z.string().cuid2(),
    status: defenderStatusSchema.optional(),
    lat: latitude.nullable().optional(),
    lon: longitude.nullable().optional(),
    altitudeM: z.number().nullable().optional(),
    headingDeg: heading.nullable().optional(),
    batteryPct: z.number().int().min(0).max(100).nullable().optional(),
});
/**
 * Met à jour l'état et/ou la position d'un défenseur. Tous les champs sont optionnels mais au moins
 * un est exigé (une requête vide ne veut rien dire) ; `null` **efface** une position connue.
 */
export const updateDefenderSchema = z
    .object({
    status: defenderStatusSchema.optional(),
    lat: latitude.nullable().optional(),
    lon: longitude.nullable().optional(),
    altitudeM: z.number().nullable().optional(),
    headingDeg: heading.nullable().optional(),
    batteryPct: z.number().int().min(0).max(100).nullable().optional(),
})
    .refine((v) => Object.values(v).some((field) => field !== undefined), {
    message: 'Au moins un champ doit être fourni.',
});
/* ------------------------------------------------------------------ *
 * Ingestion (API sécurisée par clé de source)
 * ------------------------------------------------------------------ */
/** Une menace poussée par une source. `externalRef` = son identifiant, clé d'upsert idempotent. */
export const ingestThreatSchema = z.object({
    externalRef: z.string().trim().min(1).max(200),
    class: threatClassSchema.optional(),
    level: threatLevelSchema.optional(),
    sizeM: z.number().positive().max(100).nullable().optional(),
    lat: latitude,
    lon: longitude,
    altitudeM: z.number().nullable().optional(),
    speedMps: z.number().min(0).max(1000).nullable().optional(),
    headingDeg: heading.nullable().optional(),
    /** `false` retire la menace de l'image (piste perdue / neutralisée). Défaut `true`. */
    active: z.boolean().optional(),
});
/** Position/état d'un défenseur poussé par une source. `assetId` = matériel de la flotte. */
export const ingestDefenderSchema = z.object({
    assetId: z.string().cuid2(),
    status: defenderStatusSchema.optional(),
    lat: latitude.nullable().optional(),
    lon: longitude.nullable().optional(),
    altitudeM: z.number().nullable().optional(),
    headingDeg: heading.nullable().optional(),
    batteryPct: z.number().int().min(0).max(100).nullable().optional(),
});
/** Charge utile d'ingestion : lot de menaces et/ou de positions défenseurs pour une zone. */
export const ingestPayloadSchema = z
    .object({
    threats: z.array(ingestThreatSchema).max(1000).optional(),
    defenders: z.array(ingestDefenderSchema).max(500).optional(),
})
    .refine((p) => (p.threats?.length ?? 0) + (p.defenders?.length ?? 0) > 0, {
    message: 'La charge utile doit contenir au moins une menace ou un défenseur.',
});
export const ingestResultSchema = z.object({
    threatsUpserted: z.number().int().nonnegative(),
    defendersUpserted: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ *
 * Provisionnement d'une source d'ingestion (ADMIN)
 * ------------------------------------------------------------------ */
export const createIngestionSourceSchema = z.object({
    name: z.string().trim().min(2).max(150),
    zoneId: z.string().cuid2().optional(),
});
/**
 * Réponse de création d'une source : la **clé en clair n'est renvoyée qu'ici, une seule fois** (seul
 * son haché est conservé). À transmettre au système source ; irrécupérable ensuite.
 */
export const ingestionSourceCreatedSchema = z.object({
    id: z.string(),
    name: z.string(),
    zoneId: z.string().nullable(),
    key: z.string(),
    createdAt: z.coerce.date(),
});
//# sourceMappingURL=operations.js.map